import { Component, OnDestroy } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  FormArray,
} from '@angular/forms';
import { finalize, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { DRAWER_IDS } from '../../../../../../core/constants/drawer-ids.constant';
import { DrawerService } from '../../../../../../core/services/drawer.service';
import { LoaderService } from '../../../../../../core/services/loader.service';
import { CommonService } from '../../../../../../shared/services/common.service';
import {
  FequencyTimeSlots,
  FrequencyDrawerParams,
  UnitMultiValue,
} from '../../insights-data.interface';
import { InsightsDataService } from '../../insights-data.service';
import { NzTableModule } from 'ng-zorro-antd/table';
import { CommonModule } from '@angular/common';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { ErrorResponse } from '../../../../../../core/models/error.models';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { AllowOnlyValidNumbersDirective } from '../../../../../../core/direactives/allowedOnlyValidNUmber.directive';
import _ from 'lodash';
import { TimeSlot } from '../../../../../manage-settings/manage-settings.interface';
import { NzSpinModule } from 'ng-zorro-antd/spin';
@Component({
  selector: 'app-set-duration',
  standalone: true,
  imports: [
    NzTableModule,
    ReactiveFormsModule,
    CommonModule,
    NzInputModule,
    NzButtonModule,
    NgxMaskDirective,
    AllowOnlyValidNumbersDirective,
    NzSpinModule,
  ],
  providers: [provideNgxMask()],
  templateUrl: './set-duration.component.html',
  styleUrl: './set-duration.component.scss',
})
export class SetDurationComponent implements OnDestroy {
  form!: FormGroup;
  loading$: Observable<boolean> = of(false);
  private readonly destroy$ = new Subject<void>();
  drawerParmas: FrequencyDrawerParams = {} as FrequencyDrawerParams;
  weekDays: { date: string; day: string }[] = [];
  unitValueCount = 5;
  constructor(
    private drawerService: DrawerService,
    private fb: FormBuilder,
    private loaderService: LoaderService,
    private insightsService: InsightsDataService,
    private commonService: CommonService
  ) {
    this.loading$ = this.loaderService.loading$;
    this.form = this.fb.group({
      dataItems: this.fb.array([]), // FormArray for dynamic data items
    });
    this.setupDrawerSubscription();
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<FrequencyDrawerParams>(DRAWER_IDS.DURATION)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.drawerParmas = params;
          this.weekDays = this.commonService.getWeekDates(
            new Date(this.drawerParmas.selectedDate)
          );
          // Find all records matching the selected date
          const matchedRecords = _.filter(
            this.drawerParmas.behaviorData.activityDataUnitMultiValues,
            { dataDate: this.drawerParmas.selectedDate, isDurationValue: true }
          );
          let timeSlots: TimeSlot[] | FequencyTimeSlots[] =
            this.drawerParmas.scheduleData.timeSlots;
          if (matchedRecords.length > 0) {
            timeSlots = this.insightsService.generateTimeSlots(matchedRecords);
          }

          this.addDataItem(timeSlots);
          if (matchedRecords.length > 0) {
            this.patchFormValues(timeSlots, matchedRecords);
          }
        }
      });
  }

  patchFormValues(
    timeSlots: TimeSlot[] | FequencyTimeSlots[],
    matchedRecords: UnitMultiValue[]
  ) {
    // Iterate over timeSlots
    timeSlots.forEach((element, index) => {
      // Find the matched activity by comparing the start and end time with the time slot
      const matchedActivity = matchedRecords.find(
        activity =>
          activity.startDataTime === `${element.startTime}:00` &&
          activity.endDataTime === `${element.endTime}:00` &&
          activity.dataDate === this.drawerParmas.selectedDate &&
          activity.isDurationValue
      );

      // If there's a matched activity, patch the unitValue in the form
      if (matchedActivity) {
        // Split the unitValue by commas, remove empty values and trim whitespace
        const unitValues = matchedActivity.unitValue
          .split(',')
          .map(value => value.trim()); // Filter out empty values

        // Get the form group for the corresponding time slot
        const dataItemForm = this.dataItems.at(index) as FormGroup; // Type assertion here to FormGroup

        // Access the unitValue FormArray correctly with type assertion
        const unitValueArray = dataItemForm.get('unitValue') as FormArray;

        dataItemForm.patchValue({
          id: matchedActivity.id,
        });
        // Clear the existing controls and re-add the controls based on the unit values
        unitValueArray.clear();

        unitValues.forEach(value => {
          unitValueArray.push(this.fb.control(value)); // Add new control with unitValue
        });
      }
    });
  }

  get isFormValid() {
    return this.dataItems.controls.some(group =>
      (group.get('unitValue') as FormArray).controls.some(
        control => control.value.trim() !== ''
      )
    );
  }

  get dataItems() {
    return this.form.get('dataItems') as FormArray;
  }
  get dataItemsControls(): FormGroup[] {
    return this.dataItems?.controls as FormGroup[];
  }

  unitValues(i: number) {
    return this.dataItemsControls.at(i)?.get('unitValue');
  }
  createUnitValues(): FormArray {
    return this.fb.array(
      Array(5)
        .fill('')
        .map(() => this.fb.control(''))
    );
  }
  // Method to add data items dynamically
  addDataItem(timeSlots: TimeSlot[] | FequencyTimeSlots[]) {
    if (timeSlots && timeSlots.length > 0) {
      timeSlots.forEach(element => {
        const newDataItem = this.fb.group({
          id: [0],
          dataDate: [this.drawerParmas.selectedDate],
          startDataTime: [element.startTime], // Set initial value
          endDataTime: [element.endTime], // Set initial value
          isDuration: [true], // Set based on data category
          isIntervalFrequency: [false],
          unitValue: this.createUnitValues(), // Set initial value
        });

        this.dataItems.push(newDataItem);
      });
    }
  }
  getTitle(startTime: string, endTime: string) {
    return `${startTime} - ${endTime}`;
  }

  onCancel() {
    this.drawerService.closeDrawer(DRAWER_IDS.DURATION);
  }
  submitForm() {
    const transformedData = this.transformUnitValues(this.form.value.dataItems);
    const payload = {
      unitId: this.drawerParmas.unitId,
      unitMultiValues: transformedData,
    };

    this.loaderService.show();
    const operation$ = this.insightsService.saveMultiValue(payload);

    operation$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: () => {
          this.commonService.notify(
            'success',
            `Frequency interval saved successfully`
          );
          this.onCancel();
        },
        error: error => {
          this.loaderService.hide();
          this.handleError(error);
        },
      });
  }

  private handleError({ errors, message }: ErrorResponse): void {
    const errorMessages = errors.map(
      err => err.description || err.detailedMessage
    );
    this.commonService.notify('error', errorMessages, message);
  }

  transformUnitValues(dataItems: UnitMultiValue[]) {
    return _.map(dataItems, item =>
      _.assign({}, item, {
        startDataTime: `${item.startDataTime}:00`,
        endDataTime: `${item.endDataTime}:00`,
        unitValue: _.join(
          _.map(item.unitValue, val =>
            _.isEmpty(val) || val == '0' ? '' : Number(val)
          ),
          ','
        ),
      })
    );
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  changeValue(event: any, control: any) {
    if (event?.target?.value === '') {
      control.patchValue('');
    }
  }
}
